import { Alert, Button, Input, Select, Tag, Typography, message } from "antd";
import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import {
  CLINIC_MEMORY_CATEGORIES,
  CLINIC_RULE_EFFECTS,
  type MemoryEntryDto,
  ORDER_TYPES,
  buildClinicRule,
  clinicRuleEffect,
  memoryService,
} from "../../service/memory.service";
import styles from "./memory.module.scss";

const { Title, Text } = Typography;
const { TextArea } = Input;

const ORIGIN_LABELS: Record<string, string> = {
  explicit: "You told Superdoc",
  inferred: "Noticed from your picks",
  operator: "Set by Nudge",
};

const SCOPE_LABELS: Record<string, string> = {
  user: "You",
  user_patient: "You + patient",
  clinic: "Clinic",
};

// Dev-validation surface for Superdoc memory: everything the assistant holds
// about the clinician and their practice, editable in place. Pending entries
// (inferred from repeat picks) do nothing until confirmed here or in-thread.
export const MemoryPage: React.FC = () => {
  const [entries, setEntries] = useState<MemoryEntryDto[]>([]);
  const [canEditClinic, setCanEditClinic] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [newStatement, setNewStatement] = useState("");
  const [newCategory, setNewCategory] = useState(CLINIC_MEMORY_CATEGORIES[0].value);
  // Order rules are structured: an effect, its value or the kind of order it
  // applies to, and an optional site.
  const [newEffect, setNewEffect] = useState(CLINIC_RULE_EFFECTS[0].value);
  const [newValue, setNewValue] = useState("");
  const [newOrderType, setNewOrderType] = useState(ORDER_TYPES[0].value);
  const [newLocation, setNewLocation] = useState("");
  const [creating, setCreating] = useState(false);
  // The API answers 403 when memory is not switched on for this account; that
  // is a state to explain, not an error to toast.
  const [notEnabled, setNotEnabled] = useState(false);
  // A failed list (a deploy, a blip) is not an empty memory: say so instead
  // of "Nothing remembered yet." (seen on dev during a restart, 2026-09-24).
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await memoryService.listEntries();
      setEntries(data.entries ?? []);
      setCanEditClinic(!!data.can_edit_clinic);
      setNotEnabled(false);
      setLoadError(false);
    } catch (error) {
      if ((error as { response?: { status?: number } })?.response?.status === 403) {
        setNotEnabled(true);
        return;
      }
      console.error("Error loading memory:", error);
      setLoadError(true);
      message.error("Failed to load memory");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const pending = useMemo(() => entries.filter((e) => e.status === "pending"), [entries]);
  const clinic = useMemo(() => entries.filter((e) => e.status === "active" && e.scope === "clinic"), [entries]);
  // "Yours" grouped by category label, insertion-ordered so the list is stable.
  const yoursByCategory = useMemo(() => {
    const groups = new Map<string, MemoryEntryDto[]>();
    for (const e of entries) {
      if (e.status !== "active" || e.scope === "clinic") continue;
      const key = e.category_label || e.category || "Other";
      groups.set(key, [...(groups.get(key) ?? []), e]);
    }
    return Array.from(groups.entries());
  }, [entries]);

  // Every mutation re-fetches: the backend owns status transitions (a retire
  // may open an undo window, an edit may create a replacement entry).
  const run = async (id: string, action: () => Promise<unknown>, success: string, failure: string) => {
    setBusyId(id);
    try {
      await action();
      message.success(success);
      await load();
    } catch (error) {
      console.error(failure, error);
      message.error(failure);
    } finally {
      setBusyId(null);
    }
  };

  const confirm = (e: MemoryEntryDto) =>
    run(e.id, () => memoryService.confirmEntry(e.id), "Saved to memory", "Failed to confirm");
  const dismiss = (e: MemoryEntryDto) =>
    run(e.id, () => memoryService.dismissEntry(e.id), "Dismissed", "Failed to dismiss");

  const remove = async (e: MemoryEntryDto) => {
    const result = await Swal.fire({
      title: "Delete this memory permanently?",
      text: e.statement,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#6366F1",
    });
    if (!result.isConfirmed) return;
    await run(e.id, () => memoryService.deleteEntry(e.id), "Deleted", "Failed to delete");
  };

  const startEdit = (e: MemoryEntryDto) => {
    setEditingId(e.id);
    setEditText(e.statement);
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditText("");
  };
  const saveEdit = async (e: MemoryEntryDto) => {
    const statement = editText.trim();
    if (!statement || statement === e.statement) {
      cancelEdit();
      return;
    }
    await run(e.id, () => memoryService.editEntry(e.id, statement), "Updated", "Failed to update");
    cancelEdit();
  };

  const isRule = newCategory === "orders.rule";
  const effectSpec = clinicRuleEffect(newEffect);
  const canCreate = isRule ? !effectSpec?.needsValue || !!newValue.trim() : !!newStatement.trim();

  const createClinic = async () => {
    if (!canCreate) return;
    setCreating(true);
    try {
      if (isRule) {
        const rule = buildClinicRule({
          effect: newEffect,
          value: newValue,
          location: newLocation,
          orderType: newOrderType,
        });
        await memoryService.createClinicEntry(newStatement.trim(), newCategory, rule);
      } else {
        await memoryService.createClinicEntry(newStatement.trim(), newCategory);
      }
      message.success("Clinic rule added");
      setNewStatement("");
      setNewValue("");
      setNewLocation("");
      await load();
    } catch (error) {
      console.error("Error creating clinic rule:", error);
      const detail = (error as { response?: { data?: { error?: string } } })?.response?.data?.error;
      message.error(detail ? `Failed to add clinic rule: ${detail}` : "Failed to add clinic rule");
    } finally {
      setCreating(false);
    }
  };

  // An order rule shows what it does as chips; the statement is the caption.
  const renderRule = (e: MemoryEntryDto) => {
    if (!e.rule_chips) return null;
    return (
      <div className={styles["memory__row-meta"]}>
        {e.rule_chips.when.map((chip) => (
          <Tag key={`w-${chip}`} color="blue">
            {chip}
          </Tag>
        ))}
        {e.rule_chips.when.length === 0 && <Tag color="blue">every order</Tag>}
        <Text type="secondary">→</Text>
        {e.rule_chips.then.map((chip) => (
          <Tag key={`t-${chip}`} color={e.bound ? "green" : "orange"}>
            {chip}
          </Tag>
        ))}
        {e.valid_to && <Tag>until {new Date(e.valid_to).toLocaleDateString()}</Tag>}
      </div>
    );
  };

  const renderRow = (e: MemoryEntryDto, editable: boolean) => {
    const busy = busyId === e.id;
    const editing = editingId === e.id;
    return (
      <div key={e.id} className={styles.memory__row}>
        <div className={styles["memory__row-body"]}>
          {editing ? (
            <Input
              size="small"
              value={editText}
              onChange={(ev) => setEditText(ev.target.value)}
              onPressEnter={() => saveEdit(e)}
              disabled={busy}
              autoFocus
            />
          ) : (
            <Text>{e.statement}</Text>
          )}
          {renderRule(e)}
          <div className={styles["memory__row-meta"]}>
            <Tag>{SCOPE_LABELS[e.scope] ?? e.scope}</Tag>
            <Text type="secondary">{ORIGIN_LABELS[e.origin] ?? e.origin}</Text>
          </div>
        </div>
        {editable && (
          <div className={styles["memory__row-actions"]}>
            {editing ? (
              <>
                <Button size="small" type="primary" loading={busy} onClick={() => saveEdit(e)}>
                  Save
                </Button>
                <Button size="small" disabled={busy} onClick={cancelEdit}>
                  Cancel
                </Button>
              </>
            ) : (
              <>
                <Button size="small" disabled={busy} onClick={() => startEdit(e)}>
                  Edit
                </Button>
                <Button size="small" danger loading={busy} onClick={() => remove(e)}>
                  Delete
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  if (notEnabled) {
    return (
      <div className={styles.memory__container}>
        <Title level={3} style={{ marginBottom: 4 }}>
          Memory
        </Title>
        <Text type="secondary">
          Superdoc memory isn't switched on for your account yet. Once it is, what Superdoc remembers about you and your
          practice will show here, and you can edit or delete any of it.
        </Text>
      </div>
    );
  }

  return (
    <div className={styles.memory__container}>
      <Title level={3} style={{ marginBottom: 4 }}>
        Memory
      </Title>
      <Text type="secondary">
        What Superdoc remembers about you and your practice. Edit or delete anything; pending items apply only after you
        confirm.
      </Text>

      {loadError && (
        <Alert
          type="warning"
          showIcon
          message="Couldn't load your memory just now."
          description="Nothing was changed. Try again in a moment."
          action={
            <Button size="small" onClick={load}>
              Retry
            </Button>
          }
        />
      )}

      <section className={styles.memory__section}>
        <Title level={5}>Pending</Title>
        {loading ? (
          <Text type="secondary">Loading…</Text>
        ) : loadError ? null : pending.length === 0 ? (
          <Text type="secondary">Nothing waiting for your confirmation.</Text>
        ) : (
          pending.map((e) => {
            const busy = busyId === e.id;
            return (
              <div key={e.id} className={styles.memory__row}>
                <div className={styles["memory__row-body"]}>
                  <Text>{e.statement}</Text>
                  {renderRule(e)}
                  <div className={styles["memory__row-meta"]}>
                    <Tag color="gold">Pending</Tag>
                    <Tag>{SCOPE_LABELS[e.scope] ?? e.scope}</Tag>
                    <Text type="secondary">{ORIGIN_LABELS[e.origin] ?? e.origin}</Text>
                  </div>
                </div>
                <div className={styles["memory__row-actions"]}>
                  {e.rule && !e.bound ? (
                    // A rule stated by name binds to the exact EHR item the
                    // first time that order is on a card; there is nothing to
                    // confirm before then.
                    <Tag color="orange">Applies once the EHR item is matched</Tag>
                  ) : (
                    <Button size="small" type="primary" loading={busy} onClick={() => confirm(e)}>
                      Confirm
                    </Button>
                  )}
                  <Button size="small" disabled={busy} onClick={() => dismiss(e)}>
                    Dismiss
                  </Button>
                </div>
              </div>
            );
          })
        )}
      </section>

      <section className={styles.memory__section}>
        <Title level={5}>Yours</Title>
        {!loading && !loadError && yoursByCategory.length === 0 && (
          <Text type="secondary">Nothing remembered yet.</Text>
        )}
        {yoursByCategory.map(([label, items]) => (
          <div key={label} className={styles.memory__group}>
            <Text strong>{label}</Text>
            {items.map((e) => renderRow(e, true))}
          </div>
        ))}
      </section>

      <section className={styles.memory__section}>
        <Title level={5}>Clinic</Title>
        {!loading && clinic.length === 0 && <Text type="secondary">No clinic rules.</Text>}
        {clinic.map((e) => renderRow(e, canEditClinic))}
        {canEditClinic && (
          <div className={styles.memory__form}>
            <Text strong>Add clinic rule</Text>
            {isRule && (
              <div className={styles["memory__form-actions"]}>
                <Select
                  size="small"
                  value={newEffect}
                  onChange={setNewEffect}
                  options={CLINIC_RULE_EFFECTS}
                  style={{ minWidth: 240 }}
                  disabled={creating}
                />
                {effectSpec?.byType && (
                  <Select
                    size="small"
                    value={newOrderType}
                    onChange={setNewOrderType}
                    options={ORDER_TYPES}
                    style={{ minWidth: 220 }}
                    disabled={creating}
                  />
                )}
                {effectSpec?.needsValue && (
                  <Input
                    size="small"
                    value={newValue}
                    placeholder={effectSpec.placeholder}
                    onChange={(ev) => setNewValue(ev.target.value)}
                    disabled={creating}
                    style={{ minWidth: 220 }}
                  />
                )}
                <Input
                  size="small"
                  value={newLocation}
                  placeholder="Only at this site (optional, as the EHR names it)"
                  onChange={(ev) => setNewLocation(ev.target.value)}
                  disabled={creating}
                  style={{ minWidth: 260 }}
                />
              </div>
            )}
            <TextArea
              rows={2}
              value={newStatement}
              placeholder={
                isRule
                  ? "Optional note shown with the rule, e.g. Maryville draws are picked up by LabCorp."
                  : "e.g. Annual physicals always include a lipid panel."
              }
              onChange={(ev) => setNewStatement(ev.target.value)}
              disabled={creating}
            />
            <div className={styles["memory__form-actions"]}>
              <Select
                size="small"
                value={newCategory}
                onChange={setNewCategory}
                options={CLINIC_MEMORY_CATEGORIES}
                style={{ minWidth: 200 }}
                disabled={creating}
              />
              <Button size="small" type="primary" loading={creating} disabled={!canCreate} onClick={createClinic}>
                Add
              </Button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default MemoryPage;
